import React from 'react'

import { BaseText, BaseView } from '@/components/base'
import { useTheme } from '@/hooks/useTheme'
import { DepositType } from '../../hooks/useDepositTypes'
import {
  Apple,
  Google,
  Wallet,
  Card,
  Bank,
  ChevronRight,
  ApplePay,
  GooglePay,
  QRCode,
} from '@/assets/icons'

// Маппинг иконок
const IconMap = {
  apple: Apple,
  applePay: ApplePay,
  google: Google,
  googlePay: GooglePay,
  wallet: Wallet,
  qrCode: QRCode,
  card: Card,
  cardFilled: Card,
  bank: Bank,
  dollar: Card, // Fallback to Card icon
}

export const DepositTypeItem: React.FC<DepositType> = ({
  iconName,
  iconSize = 24,
  bgIconContainer,
  title,
  description,
  flags,
  onPress,
}) => {
  const theme = useTheme()

  // Получаем компонент иконки
  const IconComponent = IconMap[iconName as keyof typeof IconMap] || Wallet

  // Определяем цвет фона контейнера иконки
  const getIconContainerBg = () => {
    // Для Apple Pay и Crypto Wallet используем черный фон
    if (iconName === 'applePay' || iconName === 'qrCode') {
      return '#000000'
    }

    if (Array.isArray(bgIconContainer)) {
      return undefined // Градиент будет применен через background
    }

    return bgIconContainer as string
  }

  // Определяем градиент для фона
  const _getIconContainerGradient = () => {
    // Для Apple Pay и Crypto Wallet не используем градиент
    if (iconName === 'applePay' || iconName === 'qrCode') {
      return undefined
    }

    if (Array.isArray(bgIconContainer)) {
      return `linear-gradient(135deg, ${bgIconContainer[0]}, ${bgIconContainer[1]})`
    }

    return undefined
  }

  // Определяем цвет иконки
  const getIconColor = () => {
    // Для Apple Pay и Crypto Wallet с черным фоном используем белую иконку
    if (iconName === 'applePay' || iconName === 'qrCode') {
      return '#ffffff'
    }

    if (Array.isArray(bgIconContainer)) {
      return '#fff'
    }
    return theme.colors.label.default
  }

  return (
    <button
      onClick={onPress}
      style={{
        backgroundColor: theme.colors.background.cardItemRoot,
        borderRadius: '34px',
        padding: '16px',
        paddingTop: '10px',
        paddingBottom: '10px',
        flexDirection: 'column',
        gap: '12px',
        border: 'none',
        cursor: 'pointer',
        width: '100%',
        display: 'flex',
      }}
    >
      {/* Main content row */}
      <BaseView row align="center" gap={12}>
        {/* Icon container with gradient or solid color */}
        <BaseView
          w={48}
          h={48}
          rounded={16}
          center
          style={{
            backgroundColor: getIconContainerBg(),
          }}
        >
          <IconComponent size={iconSize} color={getIconColor()} />
        </BaseView>

        {/* Content */}
        <BaseView flex={1} gap={4}>
          <BaseText
            className="flex"
            fontSize={18}
            fontFamily="bold"
            color="label.default"
          >
            {title}
          </BaseText>
          <BaseText
            className="text-left"
            fontSize={14}
            style={{
              lineHeight: '18px',
            }}
            fontFamily="medium"
            color="label.description"
          >
            {description}
          </BaseText>
        </BaseView>

        {/* Chevron right */}
        <BaseView w={24} center>
          <ChevronRight size={28} color={theme.colors.label.description} />
        </BaseView>
      </BaseView>

      {/* Flags */}
      {flags && flags.length > 0 && (
        <BaseView row gap={4} style={{ alignSelf: 'flex-start' }}>
          {flags.map((flag, index) => (
            <BaseView
              key={index}
              rounded={20}
              px={8}
              py={4}
              style={{
                backgroundColor: '#3A3A3C',
                lineHeight: '16px',
              }}
            >
              <BaseText
                fontSize={12}
                color={theme.colors.label.description}
                fontFamily="semibold"
              >
                {flag}
              </BaseText>
            </BaseView>
          ))}
        </BaseView>
      )}
    </button>
  )
}
